import { useDiRef } from '@/common/react/di-hooks.ts'
import { PropertyOptionsService } from '@/object-types/services/PropertyOptionService.ts'
import { useState } from 'react'
import { Box, Button, Divider, InputBase, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { globalCssVars } from '@/core/theme/GlobalCssVars.ts'
import { propertyFormConfig } from '@/objects/property-forms/PropertyFormConfig.tsx'
import type { PropertyOptionModel } from '@/generated/graphql.ts'

export interface PropertyOptionEditPopoverProps {
  option: PropertyOptionModel
  onOptionEdit: () => void
  onOptionDeleteClicked: () => void
}

const base = {
  colorFilledCircleSize: 18,
  ringThicknessPx: 2,
  circleGapPx: 2,
} as const

const config = {
  ...base,
  fullCircleSize: base.colorFilledCircleSize + 2 * (base.circleGapPx + base.ringThicknessPx),
}

const displayedOptionColors = PropertyOptionsService.defaultColors.slice(0, 9)

export function PropertyOptionEditPopover({
  option,
  onOptionEdit,
  onOptionDeleteClicked,
}: PropertyOptionEditPopoverProps) {
  const [value, setValue] = useState(option.value)
  return (
    <>
      <InputBase
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            option.value = value
            onOptionEdit()
          }
        }}
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          option.value = e.target.value
        }}
        sx={{
          width: '140px',
          height: propertyFormConfig.propHeight,
          borderRadius: globalCssVars.radius3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          m: 1,
          px: '0.5rem',
        }}
      ></InputBase>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, auto)',
          gridTemplateRows: 'repeat(3, auto)',
          gap: 0.75,
          width: 'fit-content',
        }}
      >
        {displayedOptionColors.map((x) => {
          const isSelected = x === option.color
          return (
            <Box
              key={x}
              onClick={() => {
                option.color = x
                onOptionEdit()
              }}
              sx={{
                width: config.fullCircleSize,
                height: config.fullCircleSize,
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(isSelected
                  ? {
                      border: `${config.ringThicknessPx}px solid ${x}`,
                    }
                  : {
                      '&:hover': {
                        border: (theme) =>
                          `${config.ringThicknessPx}px solid ${theme.palette.action.hover}`,
                      },
                    }),
              }}
            >
              <Box
                sx={{
                  width: config.colorFilledCircleSize,
                  height: config.colorFilledCircleSize,
                  borderRadius: '50%',
                  bgcolor: x,
                }}
              />
            </Box>
          )
        })}
      </Box>
      <Divider flexItem sx={{ mt: 1 }} />
      <Box
        sx={{
          width: '100%',
          padding: 0.75,
        }}
      >
        <Button
          sx={{
            width: '100%',
            height: '28px',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
          <Typography variant="button" fontSize={16} textTransform={'none'} alignItems={'center'}>
            Delete
          </Typography>
        </Button>
      </Box>
    </>
  )
}
